import { useState } from "react";
import { useFormContext } from "react-hook-form";

/**
 * Props for the StepCard component.
 */
interface StepCardProps {
  /** Step title/name */
  readonly title: string;
  /** Step type (START, END, GATEWAY_EXCLUSIVE, TASK, etc.) */
  readonly nodeType?: string;
  /** Step description */
  readonly description?: string;
  /** Names of next steps in the flow */
  readonly nextSteps?: string[];
  /** Index of the step in the nodes array */
  readonly stepIndex: number;
}

/**
 * Renders the appropriate icon based on node type.
 */
function NodeIcon({ nodeType }: { readonly nodeType: string }) {
  if (nodeType === "START") {
    return (
      <div className="w-5 h-5 rounded-full border-2 border-[#18181b] bg-white" />
    );
  }
  if (nodeType === "GATEWAY_EXCLUSIVE") {
    return (
      <div className="w-5 h-5 border-2 border-[#18181b] bg-white rounded-sm transform rotate-45" />
    );
  }
  if (nodeType === "END") {
    return (
      <div className="w-5 h-5 rounded-full border-2 border-pink-400 bg-white" />
    );
  }
  return (
    <div className="w-5 h-5 border-2 border-[#18181b] bg-white rounded-sm" />
  );
}

/**
 * StepCard component displays a single process step with icon, title, description,
 * and next step information. Supports inline editing of title and description.
 *
 * @param props - Component props
 * @returns Process step card UI
 */
export default function StepCard({
  title,
  nodeType = "TASK",
  description,
  nextSteps = [],
  stepIndex,
}: StepCardProps) {
  const { watch, setValue, register } = useFormContext();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);

  const stepTitle = watch(`nodes.${stepIndex}.name`) || title;
  const stepDescription =
    watch(`nodes.${stepIndex}.description`) || description;

  // Handle title change to update both name and label
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(`nodes.${stepIndex}.name`, newValue);
    setValue(`nodes.${stepIndex}.label`, newValue);
  };

  return (
    <div className="bg-[#ffffff] border border-[#e4e4e7] rounded-lg p-3 group/card hover:border-[#d4d4d8] transition-colors">
      <div className="flex items-start gap-3">
        <div className="flex items-center self-stretch">
          <div className="w-2.5 h-full bg-[#d4d4d8] rounded-sm" />
        </div>

        <div className="flex-1 space-y-5">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <NodeIcon nodeType={nodeType} />
              </div>
              {isEditingTitle ? (
                <input
                  value={stepTitle}
                  onChange={handleTitleChange}
                  autoFocus
                  onBlur={() => setIsEditingTitle(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Escape") {
                      setIsEditingTitle(false);
                    }
                  }}
                  className="text-sm font-semibold bg-white border border-[#e4e4e7] rounded px-2 py-1 flex-1 focus:outline-none focus:border-[#d4d4d8]"
                />
              ) : (
                <h4
                  className="text-sm text-[#18181b] font-semibold cursor-pointer hover:bg-[#f4f4f5] rounded px-2 py-1 -mx-2 transition-colors flex-1"
                  onClick={() => setIsEditingTitle(true)}
                  title="Click to edit"
                >
                  {stepTitle}
                </h4>
              )}
            </div>
            {isEditingDescription ? (
              <textarea
                {...register(`nodes.${stepIndex}.description`)}
                autoFocus
                onBlur={() => setIsEditingDescription(false)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setIsEditingDescription(false);
                  }
                }}
                className="text-sm text-[#71717a] leading-relaxed bg-white border border-[#e4e4e7] rounded px-2 py-1 w-full focus:outline-none focus:border-[#d4d4d8] min-h-[60px] resize-y"
                rows={2}
              />
            ) : (
              <p
                className="text-sm text-[#71717a] leading-relaxed cursor-pointer hover:bg-[#f4f4f5] rounded px-2 py-1 -mx-2 transition-colors"
                onClick={() => setIsEditingDescription(true)}
                title="Click to edit"
              >
                {stepDescription || "Click to add a description..."}
              </p>
            )}
          </div>

          {nextSteps.length > 0 && (
            <div className="flex items-start gap-1">
              <span className="text-xs text-[#3f3f46] shrink-0">
                nächster Schritt: {nextSteps.join(" → ")}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
